/*
 * Copyright (c) 2020. Dave van Rijn Development
 */

import * as Sentry from '@sentry/react';
import {ImageSources} from './interfaces';
import {BaseModel} from '../models/baseModel';

export const PASSWORD_RULES = {
    MIN_LENGTH: 8, MAX_LENGTH: 24, CONTAIN_NUMBER: true, CONTAIN_SYMBOL: true, CONTAIN_CAPITAL: true
};

interface Validation {
    MIN_LENGTH: boolean,
    MAX_LENGTH: boolean,
    CONTAIN_NUMBER: boolean,
    CONTAIN_CAPITAL: boolean,
    CONTAIN_SYMBOL: boolean,
}

export const validatePassword = (password: string): Validation => {
    const {MIN_LENGTH, MAX_LENGTH, CONTAIN_CAPITAL, CONTAIN_NUMBER, CONTAIN_SYMBOL} = PASSWORD_RULES;
    return {
        MIN_LENGTH: password.length >= MIN_LENGTH,
        MAX_LENGTH: password.length <= MAX_LENGTH,
        CONTAIN_NUMBER: !CONTAIN_NUMBER || /\d/.test(password),
        CONTAIN_CAPITAL: !CONTAIN_CAPITAL || /[A-Z]/.test(password),
        CONTAIN_SYMBOL: !CONTAIN_SYMBOL || /[!.\/?@#$%^\\&*(),<>`~\-_=+]/.test(password)
    };
};

export function getImg(imgName: string, fallbackExtension: string = 'png'): ImageSources {
    let webpPath: string | null = null;
    let fallbackPath: string = '/' + require(`../../res/img/${imgName}.${fallbackExtension}`);
    try {
        webpPath = '/' + require(`../../res/img/${imgName}.webp`);
    } catch {
    }
    const sources: Array<[string, string]> = [];
    if (webpPath) sources.push([webpPath, 'image/webp']);
    sources.push([fallbackPath, getFallbackImageType(fallbackExtension)]);
    return sources;
}

function getFallbackImageType(extension: string) {
    if (extension === 'png') return 'image/png';
    else if (/jpe?g/.test(extension)) return 'image/jpeg';
    else if (extension === 'webp') return 'image/webp';
    return '';
}

export const createErrorMessage = (message: string): string =>
    `${message} Probeer het later opnieuw of neem contact met ons op.`;

export function deepClone(obj: any, identityMap: Map<string, any> = new Map()): any {
    if (obj === undefined || obj === null) return obj;
    else if (Array.isArray(obj)) return obj.map((_obj: any) => deepClone(_obj, identityMap));
    else if (obj instanceof BaseModel) {
        if (identityMap?.has(obj.identity)) return identityMap.get(obj.identity);
        const cloned = obj.clone(identityMap);
        identityMap = identityMap.set(cloned.identity, cloned);
        return cloned;
    } else if (isObject(obj)) {
        const cloned: { [key: string]: any } = {};
        for (const [key, value] of Object.entries(obj)) cloned[key] = deepClone(value, identityMap);
        return cloned;
    }
    return obj;
}

function isObject(val: any): boolean {
    return typeof val === 'object' && !Array.isArray(val) && val !== null && !(val instanceof BaseModel);
}

export function captureException(error: Error) {
    Sentry.captureException(error);
}